import { supabase } from './supabaseClient';

export interface CivicLevel {
  key: string;
  label: string;
  order_index: number;
  description: string | null;
  xp_threshold: number;
  created_at: string;
  updated_at: string;
}

export interface SongActionModal {
  id: string;
  song_id: string;
  civic_level_key: string | null;
  title: string;
  body: string;
  cta_label: string;
  cta_type: 'link' | 'action' | 'none';
  cta_url: string | null;
  status: 'active' | 'draft' | 'archived';
  priority: number;
  created_at: string;
  updated_at: string;
}

export async function getCivicLevels(): Promise<CivicLevel[]> {
  const { data, error } = await supabase
    .from('civic_levels')
    .select('*')
    .order('order_index', { ascending: true });

  if (error) {
    console.error('[actionModalService] Error fetching civic levels:', error);
    throw error;
  }

  return data || [];
}

export async function getSongActionModal(
  songId: string,
  civicLevelKey: string | null
): Promise<SongActionModal | null> {
  // Try to get level-specific modal first
  if (civicLevelKey) {
    const { data: levelModal, error: levelError } = await supabase
      .from('song_action_modals')
      .select('*')
      .eq('song_id', songId)
      .eq('civic_level_key', civicLevelKey)
      .eq('status', 'active')
      .order('priority', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (levelError) {
      console.error('[actionModalService] Error fetching level-specific modal:', levelError);
    }

    if (levelModal) {
      return levelModal;
    }
  }

  // Fallback to default modal (civic_level_key is null)
  const { data: defaultModal, error: defaultError } = await supabase
    .from('song_action_modals')
    .select('*')
    .eq('song_id', songId)
    .is('civic_level_key', null)
    .eq('status', 'active')
    .order('priority', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (defaultError) {
    console.error('[actionModalService] Error fetching default modal:', defaultError);
    return null;
  }

  return defaultModal;
}

export async function getAllSongActionModals(songId: string): Promise<SongActionModal[]> {
  const { data, error } = await supabase
    .from('song_action_modals')
    .select('*')
    .eq('song_id', songId)
    .order('priority', { ascending: false });

  if (error) {
    console.error('[actionModalService] Error fetching all modals:', error);
    throw error;
  }

  return data || [];
}

export async function createSongActionModal(
  modal: Omit<SongActionModal, 'id' | 'created_at' | 'updated_at'>
): Promise<SongActionModal> {
  const { data, error } = await supabase
    .from('song_action_modals')
    .insert(modal)
    .select()
    .single();

  if (error) {
    console.error('[actionModalService] Error creating modal:', error);
    throw error;
  }

  return data;
}

export async function updateSongActionModal(
  id: string,
  updates: Partial<Omit<SongActionModal, 'id' | 'created_at' | 'updated_at'>>
): Promise<SongActionModal> {
  const { data, error } = await supabase
    .from('song_action_modals')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[actionModalService] Error updating modal:', error);
    throw error;
  }

  return data;
}

export async function deleteSongActionModal(id: string): Promise<void> {
  const { error } = await supabase
    .from('song_action_modals')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[actionModalService] Error deleting modal:', error);
    throw error;
  }
}

export async function upsertSongActionModal(
  songId: string,
  civicLevelKey: string | null,
  modalData: Partial<SongActionModal>
): Promise<SongActionModal> {
  // Check if modal already exists
  const { data: existing } = await supabase
    .from('song_action_modals')
    .select('id')
    .eq('song_id', songId)
    .eq('civic_level_key', civicLevelKey || null)
    .maybeSingle();

  if (existing) {
    // Update existing modal
    return updateSongActionModal(existing.id, modalData);
  } else {
    // Create new modal
    return createSongActionModal({
      song_id: songId,
      civic_level_key: civicLevelKey,
      title: modalData.title || '',
      body: modalData.body || '',
      cta_label: modalData.cta_label || 'Take Action',
      cta_type: modalData.cta_type || 'action',
      cta_url: modalData.cta_url || null,
      status: modalData.status || 'active',
      priority: modalData.priority || 0,
    });
  }
}
